import { useRef } from 'react';
import { PulseUser } from './usePulseSocket';

const WINDOW_MS = 2000; // 2-second rolling window
const MAX_KPS = 8;      // 8 keystrokes/sec = full velocity

/**
 * Tracks typing velocity (0–1) for all users by measuring buffer-change rate.
 * Returns a Map<userId, velocity> updated every render frame.
 */
export function useVelocity(
  users: Map<string, PulseUser>,
  localBufferLen: number,
  localId: string,
): Map<string, number> {
  const tsRef = useRef(new Map<string, number[]>());
  const prevLenRef = useRef(new Map<string, number>());

  const now = Date.now();
  const cutoff = now - WINDOW_MS;

  // Track remote user buffer growth
  for (const [id, user] of users) {
    const prev = prevLenRef.current.get(id) ?? 0;
    if (user.buffer.length > prev) {
      const arr = tsRef.current.get(id) || [];
      const added = Math.min(user.buffer.length - prev, 4);
      for (let i = 0; i < added; i++) arr.push(now);
      tsRef.current.set(id, arr);
    }
    prevLenRef.current.set(id, user.buffer.length);
  }

  // Track local user buffer growth
  const prevLocal = prevLenRef.current.get(localId) ?? 0;
  if (localBufferLen > prevLocal) {
    const arr = tsRef.current.get(localId) || [];
    const added = Math.min(localBufferLen - prevLocal, 4);
    for (let i = 0; i < added; i++) arr.push(now);
    tsRef.current.set(localId, arr);
  }
  prevLenRef.current.set(localId, localBufferLen);

  // Build velocity map, prune expired timestamps
  const velocities = new Map<string, number>();
  for (const [id, ts] of tsRef.current) {
    const recent = ts.filter(t => t > cutoff);
    tsRef.current.set(id, recent);
    if (recent.length === 0) {
      velocities.set(id, 0);
    } else {
      const kps = recent.length / (WINDOW_MS / 1000);
      velocities.set(id, Math.min(1, kps / MAX_KPS));
    }
  }

  return velocities;
}
